/**
 * La lettura del giornale, e i filtri che vivono nell'indirizzo.
 *
 * **I filtri stanno nell'URL, non nello stato del componente.** Non e' una
 * comodita': una ricerca nel giornale e' il piu' delle volte una domanda che si
 * gira a qualcun altro — «guarda chi ha cambiato questa soglia il 12» — e uno
 * stato interno rende quel collegamento inutile, perche' chi lo apre vede
 * l'elenco intero. Con i filtri nell'indirizzo il collegamento *e'* la domanda,
 * e un ricaricamento della pagina non la perde.
 *
 * **Non c'e' nessuna funzione che scriva.** Il giornale e' di sola lettura, e
 * non per dimenticanza: un evento che si puo' correggere non prova niente. La
 * lettura non registra nemmeno se stessa.
 *
 * **I tetti li dichiara il servizio.** `max_size` e `max_export` arrivano nella
 * risposta: la pagina non ne tiene una copia, perche' una copia che invecchia
 * farebbe chiedere pagine che vengono rifiutate — o, peggio, farebbe credere
 * completo un elenco troncato.
 */
import { download, get } from '../api'

export interface AuditEvent {
  id: string
  at: string
  actor_id: string | null
  action: string
  subject_type: string | null
  subject_id: string | null
  /** Passato dalla redazione ricorsiva del servizio: qui non arrivano segreti. */
  detail: unknown
}

export interface AuditPage {
  events: AuditEvent[]
  page: number
  size: number
  total: number
  pages: number
  max_size: number
  max_export: number
}

/** Che cosa si sta cercando. Le date sono giorni (AAAA-MM-GG) e non istanti:
 *  un'ora con fuso dentro un indirizzo e' cio' che rende due collegamenti
 *  apparentemente uguali due ricerche diverse. */
export interface AuditFilter {
  since: string
  until: string
  actor: string
  action: string
  subject_type: string
  subject_id: string
  page: number
  size: number
}

export const DEFAULT_SIZE = 50

const DAY = /^\d{4}-\d{2}-\d{2}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// I nomi delle azioni e dei soggetti: lettere, cifre, punto, trattino, underscore.
// Cio' che viene dall'indirizzo finisce in una richiesta al servizio, e non e'
// nostro finche' non si e' guardato.
const TOKEN = /^[A-Za-z0-9._-]{1,64}$/

function keptDay(value: string | null): string {
  return value !== null && DAY.test(value) ? value : ''
}

function keptToken(value: string | null): string {
  return value !== null && TOKEN.test(value) ? value : ''
}

function keptNumber(value: string | null, fallback: number, max: number): number {
  if (value === null) return fallback
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > max) return fallback
  return parsed
}

/**
 * I filtri ricostruiti dall'indirizzo.
 *
 * Cio' che non ha la forma attesa si scarta invece di essere passato al
 * servizio: un indirizzo copiato a mano, o accorciato da un programma di posta,
 * deve dare un elenco piu' largo e non un errore. Le date malformate le
 * rifiuta comunque il servizio, ma farlo anche qui evita di trasformare un
 * collegamento storto in una schermata di errore.
 */
export function parseAuditFilter(search: string): AuditFilter {
  const query = new URLSearchParams(search)
  const actor = query.get('actor')
  return {
    since: keptDay(query.get('since')),
    until: keptDay(query.get('until')),
    actor: actor !== null && UUID.test(actor) ? actor : '',
    action: keptToken(query.get('action')),
    subject_type: keptToken(query.get('subject_type')),
    subject_id: keptToken(query.get('subject_id')),
    page: keptNumber(query.get('page'), 1, 10_000),
    // Il tetto vero e' del servizio; qui c'e' solo un limite di forma, perche'
    // un `size` assurdo nell'indirizzo non deve nemmeno partire.
    size: keptNumber(query.get('size'), DEFAULT_SIZE, 1000),
  }
}

/**
 * L'indirizzo che corrisponde a questi filtri.
 *
 * I campi vuoti non compaiono: `?action=&actor=` sarebbe un indirizzo diverso
 * da `?` per la stessa ricerca, e due collegamenti diversi per la stessa
 * domanda si accumulano nei messaggi di chi indaga. L'ordine e' fisso perche'
 * lo stesso filtro deve dare la stessa stringa — altrimenti «e' lo stesso
 * collegamento?» diventa una domanda da confrontare a mano.
 */
export function serializeAuditFilter(filter: AuditFilter): string {
  const query = new URLSearchParams()
  if (filter.since !== '') query.set('since', filter.since)
  if (filter.until !== '') query.set('until', filter.until)
  if (filter.actor !== '') query.set('actor', filter.actor)
  if (filter.action !== '') query.set('action', filter.action)
  if (filter.subject_type !== '') query.set('subject_type', filter.subject_type)
  if (filter.subject_id !== '') query.set('subject_id', filter.subject_id)
  if (filter.page !== 1) query.set('page', String(filter.page))
  if (filter.size !== DEFAULT_SIZE) query.set('size', String(filter.size))
  const written = query.toString()
  return written === '' ? '' : `?${written}`
}

/** I soli filtri, senza impaginazione: e' cio' che l'esportazione accetta. */
function criteria(filter: AuditFilter): URLSearchParams {
  const query = new URLSearchParams()
  if (filter.since !== '') query.set('since', filter.since)
  if (filter.until !== '') query.set('until', filter.until)
  if (filter.actor !== '') query.set('actor', filter.actor)
  if (filter.action !== '') query.set('action', filter.action)
  if (filter.subject_type !== '') query.set('subject_type', filter.subject_type)
  if (filter.subject_id !== '') query.set('subject_id', filter.subject_id)
  return query
}

export function readAudit(filter: AuditFilter): Promise<AuditPage> {
  const query = criteria(filter)
  query.set('page', String(filter.page))
  query.set('size', String(filter.size))
  return get<AuditPage>(`/audit?${query.toString()}`)
}

/** Il foglio degli eventi filtrati.
 *
 * Gli *stessi* filtri dell'elenco che si sta guardando: una esportazione che
 * rispondesse a una domanda diversa da quella a schermo e' il modo in cui si
 * porta a una riunione un dato che non si e' mai visto. */
export function exportAudit(filter: AuditFilter): Promise<Blob> {
  const query = criteria(filter).toString()
  return download(`/audit/export${query === '' ? '' : `?${query}`}`)
}
